import React from 'react';
import { List, LayoutGrid, Download } from 'lucide-react';
import { useIsMobile } from '../../hooks/useIsMobile';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Source card image mapping by partition.
// `default` is the generic KG icon so unknown partitions render sensibly.
// Add partition-specific entries here to surface subject-matter imagery
// (e.g. country flag, sector icon).
const FLAG_CONFIG = {
  canada_google_antitrust: { src: '/icons/canada-flag-img.png', alt: 'Canada', width: 94, height: 63 },
  canada: { src: '/icons/canada-flag-img.png', alt: 'Canada', width: 94, height: 63 },
  default: { src: '/icons/knowledge-graph.png', alt: 'Document', width: 88, height: 88 }
};

const getFlag = (partition) => {
  return FLAG_CONFIG[partition] || FLAG_CONFIG.default;
};

export const SourcesSection = ({ 
  sourceDocuments, 
  sourcesViewMode, 
  setSourcesViewMode, 
  onDownloadSource,
  idx 
}) => {
  const isMobile = useIsMobile(640);
  
  if (!sourceDocuments || sourceDocuments.length === 0) return null;

  const circleSize = isMobile ? 'w-20 h-20' : 'w-48 h-48';
  const flagScale = isMobile ? 0.5 : 1;

  return (
    <div className="mb-5 pb-4 border-b border-gray-400/30" data-testid={`sources-section-${idx}`}>
      {/* Header with toggle */}
      <div className="flex items-center justify-between mb-3">
        <h4 className={`font-bold text-black ${isMobile ? 'text-sm' : 'text-base'} flex items-center gap-2`}>
          <img
            src="/icons/knowledge-graph.png"
            alt="Knowledge Graph"
            className="h-5 w-5 object-contain"
            data-testid="sources-header-kg-icon"
          />
          I&apos;ve found ({sourceDocuments.length}) resources in your knowledge graph
        </h4>
        <div className="flex items-center gap-1 bg-gray-200/60 rounded-lg p-1" data-testid="sources-view-toggle">
          <button
            onClick={() => setSourcesViewMode('list')}
            className={`p-1.5 rounded transition-colors ${sourcesViewMode === 'list' ? 'bg-white shadow-sm text-cyan-600' : 'text-gray-500 hover:text-gray-700'}`}
            title="List view"
            data-testid="sources-list-view-btn"
          >
            <List className="h-4 w-4" />
          </button>
          <button
            onClick={() => setSourcesViewMode('circles')}
            className={`p-1.5 rounded transition-colors ${sourcesViewMode === 'circles' ? 'bg-white shadow-sm text-cyan-600' : 'text-gray-500 hover:text-gray-700'}`}
            title="Circle view"
            data-testid="sources-circle-view-btn"
          >
            <LayoutGrid className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* List View */}
      {sourcesViewMode === 'list' && (
        <div className="space-y-2" data-testid="sources-list-view">
          {sourceDocuments.map((doc, dIdx) => {
            const flag = getFlag(doc.partition);
            return (
              <div 
                key={dIdx} 
                className="flex items-center gap-3 p-2 rounded-lg bg-white/40 hover:bg-white/60 transition-colors cursor-pointer" 
                data-testid={`source-item-${dIdx}`}
                onClick={() => onDownloadSource(doc)}
                title={`Click to download: ${doc.filename}`}
              >
                <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden" style={{ backgroundColor: '#00d4ff' }}>
                  <img 
                    src={flag.src} 
                    alt={flag.alt} 
                    className="w-8 h-8 object-contain"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 hover:text-cyan-600 transition-colors truncate">{doc.filename}</p>
                  {doc.page_number && <p className="text-xs text-gray-500">Page {doc.page_number}</p>}
                </div>
                <Download className="h-4 w-4 text-gray-400 flex-shrink-0" />
              </div>
            );
          })}
        </div>
      )}

      {/* Circle View */}
      {sourcesViewMode === 'circles' && (
        <div className="pb-2" data-testid="sources-circle-view">
          <div className={isMobile ? "flex gap-3 overflow-x-auto pb-2 -webkit-overflow-scrolling-touch" : "flex justify-between pr-2"}>
            {sourceDocuments.slice(0, 5).map((doc, dIdx) => {
              const flag = getFlag(doc.partition);
              return (
                <div 
                  key={dIdx} 
                  className="source-circle-item flex flex-col items-center gap-3 cursor-pointer group flex-shrink-0"
                  style={{ width: isMobile ? '90px' : '18%' }}
                  data-testid={`source-circle-${dIdx}`}
                  onClick={() => onDownloadSource(doc)}
                  title={`Click to download: ${doc.filename}`}
                >
                  <div 
                    className={`${circleSize} rounded-full flex items-center justify-center shadow-lg transition-transform duration-300 ease-out ${isMobile ? '' : 'group-hover:scale-150 group-hover:shadow-2xl group-hover:z-10'} relative overflow-hidden`}
                    style={{ backgroundColor: '#00d4ff' }}
                  >
                    <div className="absolute inset-0 rounded-full shimmer-glass-subtle z-20" style={{ '--wisp-delay': `${dIdx * 2}s` }}></div>
                    <img 
                      src={flag.src} 
                      alt={flag.alt} 
                      className="object-contain z-10"
                      style={{ width: `${flag.width * flagScale}px`, height: `${flag.height * flagScale}px` }}
                    />
                    <div className={`absolute top-1 right-1 ${isMobile ? 'w-5 h-5 text-xs' : 'w-8 h-8 text-sm'} rounded-full bg-black text-white font-bold flex items-center justify-center z-30`}>
                      {dIdx + 1}
                    </div>
                  </div>
                  <p className={`text-center font-bold leading-tight transition-all duration-300 group-hover:font-bold ${isMobile ? 'text-xs w-[80px]' : 'text-xs w-full max-w-[160px]'}`} style={{ color: '#000000', overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' }}>
                    [{dIdx + 1}] {doc.filename.replace(/\.pdf|\.pptx|\.xlsx|\.docx/gi, '').replace(/_/g, ' ')}
                  </p>
                </div>
              );
            })}
          </div>
          {sourceDocuments.length > 5 && (
            <p className="text-xs text-gray-500 mt-2">+{sourceDocuments.length - 5} more sources</p>
          )}
        </div>
      )}
    </div>
  );
};

export default SourcesSection;
